import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";
import { HEBREW_PRACTICE_PATH } from "common/practiceLocale";
import { restoreSessionFromCookie } from "../composables/restoreSession";
import useSeo from "../composables/useSeo";
import { practiceRouteSeo } from "../i18n/practiceCopy";
import { trackMetaPixelPageView } from "../helpers/metaPixelHelper";
import { trackPageView } from "../helpers/pageView";
import {
  GRADE_HUBS,
  gradeHubFromPath,
  gradeHubPath,
  gradeHubView,
  topicBreadcrumbs,
} from "common/practiceGradeTopics";
import {
  hubCopy,
  isMathTopicPath,
  mathTopicPath,
  topicFromPath,
} from "../seo/topicPages";

/**
 * Extended meta interface to include SEO information
 */
declare module "vue-router" {
  interface RouteMeta {
    requiresAuth?: boolean;
    title?: string;
    description?: string;
    keywords?: string;
    locale?: string;
    breadcrumbs?: Array<{ name: string; url: string }>;
  }
}

const ORIGIN = "https://themathboard.com";
const hebrewPracticeSeo = practiceRouteSeo("practiceHe", true)!;
const PRACTICE_HREFLANG = [
  { hreflang: "en", href: `${ORIGIN}/practice` },
  { hreflang: "he", href: `${ORIGIN}${HEBREW_PRACTICE_PATH}` },
  { hreflang: "x-default", href: `${ORIGIN}/practice` },
];
const INDEX_ROBOTS =
  "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1";
const enHub = hubCopy("en");
const heHub = hubCopy("he");
const HUB_HREFLANG = [
  { hreflang: "en", href: `${ORIGIN}/math` },
  { hreflang: "he", href: `${ORIGIN}/he/math` },
  { hreflang: "x-default", href: `${ORIGIN}/math` },
];
const INDEXABLE_PATHS = new Set([
  "/",
  "/practice",
  HEBREW_PRACTICE_PATH,
  "/practice/blank",
  "/registerTeacher",
  "/registerStudent",
  "/math",
  "/he/math",
]);
for (const hub of GRADE_HUBS) {
  INDEXABLE_PATHS.add(gradeHubPath(hub.grade, "en"));
  INDEXABLE_PATHS.add(gradeHubPath(hub.grade, "he"));
}

function gradeHreflang(grade: number) {
  const en = `${ORIGIN}${gradeHubPath(grade, "en")}`;
  const he = `${ORIGIN}${gradeHubPath(grade, "he")}`;
  return [
    { hreflang: "en", href: en },
    { hreflang: "he", href: he },
    { hreflang: "x-default", href: en },
  ];
}

const gradeRoutes: RouteRecordRaw[] = GRADE_HUBS.flatMap((hub) => {
  const en = gradeHubView(hub.grade, "en");
  const he = gradeHubView(hub.grade, "he");
  return [
    {
      path: gradeHubPath(hub.grade, "en"),
      component: () => import("../components/GradeHub.vue"),
      name: `grade-${hub.grade}`,
      meta: {
        requiresAuth: false,
        title: en?.title,
        description: en?.description,
        keywords: en?.keywords,
      },
    },
    {
      path: gradeHubPath(hub.grade, "he"),
      component: () => import("../components/GradeHub.vue"),
      name: `grade-${hub.grade}-he`,
      meta: {
        requiresAuth: false,
        title: he?.title,
        description: he?.description,
        keywords: he?.keywords,
        locale: "he_IL",
      },
    },
  ];
});
/** True after a topic route pinned robots, so the next public page can restore them. */
let topicRobotsPinned = false;

const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    component: () => import("@/components/Welcome.vue"),
    name: "main",
    meta: {
      requiresAuth: false,
      title: "Free Online Math Whiteboard for Teachers & Students | MathBoard",
      description:
        "Free online math whiteboard for teachers and students. Teach a live class or practice with an AI coach — a math board for teaching and solo study, with no download.",
      keywords:
        "math whiteboard, mathwhiteboard, math board, maths whiteboard, math whiteboard online, online whiteboard for math, math board for teaching, free online whiteboard for teaching math, AI practice coach",
    },
    props: true,
  },

  {
    path: "/login",
    component: () => import("@/components/Welcome.vue"),
    name: "login",
    meta: {
      requiresAuth: false,
      title: "Login - MathBoard",
      description:
        "Log in to your MathBoard account to create lessons, manage questions, review student answers, and facilitate collaborative mathematics learning.",
      keywords: "login, sign in, mathematics learning, teacher tools",
    },
  },
  {
    path: "/registerTeacher",
    component: () => import("@/components/Welcome.vue"),
    name: "registerTeacher",
    meta: {
      requiresAuth: false,
      title: "Teacher Registration - MathBoard",
      description:
        "Register as a teacher on MathBoard to create custom mathematics lessons, design questions, review student answers, and manage collaborative learning activities.",
      keywords:
        "teacher registration, educator, math teaching, create lessons, question design",
    },
    props: true,
  },
  {
    path: "/registerStudent",
    component: () => import("@/components/Welcome.vue"),
    name: "registerStudent",
    meta: {
      requiresAuth: false,
      title: "Student Registration - MathBoard",
      description:
        "Register as a student on MathBoard to access teacher-created lessons, answer questions, collaborate with peers, and receive feedback on your mathematics work.",
      keywords:
        "student registration, math learning, education, collaborative learning",
    },
    props: true,
  },
  {
    path: "/lessons",
    component: () => import("@/components/Lessons.vue"),
    name: "lessons",
    meta: {
      requiresAuth: true,
      title: "Math Lessons - MathBoard",
      description:
        "Browse and access teacher-created mathematics lessons designed for interactive learning and collaboration.",
      keywords:
        "math lessons, education, teaching resources, teacher created content",
    },
  },
  {
    path: "/lesson/:lessonUUId",
    component: () => import("@/components/Lesson.vue"),
    name: "lesson",
    meta: {
      requiresAuth: true,
      title: "Math Lesson - MathBoard",
      description:
        "View and interact with teacher-created mathematics lesson content, featuring collaborative tools and interactive learning activities.",
      keywords: "math lesson, teaching, education, interactive learning",
    },
  },
  {
    path: "/practice",
    component: () => import("@/components/PracticeQuestions.vue"),
    name: "practice",
    meta: {
      requiresAuth: false,
      title: "AI Math Tutor — Practice on Math Whiteboard",
      description:
        "Solo math practice on an interactive board. Pick a subject or start a blank sheet, check answers, and get tips or spoken help. Progress is saved to the account when signed in, and saved in the browser for guests.",
      keywords:
        "AI math tutor, math practice, self-study, interactive math board, check answers, voice coach",
    },
  },
  {
    path: HEBREW_PRACTICE_PATH,
    component: () => import("@/components/PracticeQuestions.vue"),
    name: "practiceHe",
    meta: {
      requiresAuth: false,
      title: hebrewPracticeSeo.title,
      description: hebrewPracticeSeo.description,
      keywords: hebrewPracticeSeo.keywords,
      locale: "he_IL",
    },
  },
  {
    path: "/practice/blank",
    component: () => import("@/components/Practice.vue"),
    name: "practiceBlank",
    meta: {
      requiresAuth: false,
      title: "Blank Practice Sheet — AI Math Tutor",
      description:
        "Practice on a blank board with an AI tutor: write or paste a question, or upload a worksheet image. Work stays on this device.",
      keywords:
        "AI math tutor, blank sheet, paste text, worksheet image, math practice",
    },
  },
  {
    path: "/practice/together/:sessionId",
    component: () => import("@/components/Practice.vue"),
    name: "practiceTogether",
    meta: {
      requiresAuth: false,
      title: "Shared Practice — Math Whiteboard",
      description: "Work together on a practice board.",
      keywords: "math practice, shared board",
    },
  },
  {
    path: "/practice/:questionUUId",
    component: () => import("@/components/Practice.vue"),
    name: "practiceQuestion",
    meta: {
      requiresAuth: false,
      title: "Practice — AI Math Tutor",
      description: "Work on a practice question with local scratch work and AI tutor tips.",
      keywords: "AI math tutor, math practice, problem solving",
    },
  },
  {
    path: "/questions",
    component: () => import("@/components/Questions.vue"),
    name: "questions",
    meta: {
      requiresAuth: true,
      title: "Math Questions - MathBoard",
      description:
        "Access teacher-designed mathematics questions to practice problem-solving skills and test understanding through collaborative learning.",
      keywords: "math questions, practice, assessment, teacher created",
    },
  },
  {
    path: "/question/:questionUUId",
    component: () => import("@/components/Question.vue"),
    name: "question",
    meta: {
      requiresAuth: true,
      title: "Mathematics Question - MathBoard",
      description:
        "Work on teacher-designed mathematics questions and improve your problem-solving skills through collaborative learning and feedback.",
      keywords: "math question, practice, problem solving, teacher created",
    },
  },
  {
    path: "/answers",
    component: () => import("@/components/Answers.vue"),
    name: "answers",
    meta: {
      requiresAuth: true,
      title: "Math Answers - MathBoard",
      description:
        "View and review student answers to teacher-created mathematics questions, providing feedback and assessment.",
      keywords: "math answers, solutions, review, teacher feedback",
    },
  },
  {
    path: "/answer/:answerUUId",
    component: () => import("@/components/Answer.vue"),
    name: "answer",
    meta: {
      requiresAuth: true,
      title: "Mathematics Answer - MathBoard",
      description:
        "Review detailed student answers to teacher-created mathematics questions and provide constructive feedback.",
      keywords: "math answer, solution, explanation, teacher review",
    },
  },
  {
    path: "/reset-password",
    name: "reset-password",
    component: () => import("../components/ResetPassword.vue"),
    meta: {
      requiresAuth: false,
      title: "Reset Password - MathBoard",
      description: "Reset your MathBoard account password.",
      keywords: "password reset, account recovery",
    },
  },
  {
    path: "/uploadPhoto/:lessonUUId/:userUUId",
    name: "uploadPhoto",
    props: true,
    component: () => import("../components/UploadPhoto.vue"),
    meta: {
      requiresAuth: false,
      title: "Upload Photo - MathBoard",
      description: "Upload a photo for your mathematics work.",
      keywords: "upload, photo, math work",
    },
  },
  {
    path: "/math",
    component: () => import("../components/MathTopics.vue"),
    name: "mathTopics",
    meta: {
      requiresAuth: false,
      title: enHub.title,
      description: enHub.description,
      keywords: enHub.keywords,
    },
  },
  {
    path: "/he/math",
    component: () => import("../components/MathTopics.vue"),
    name: "mathTopicsHe",
    meta: {
      requiresAuth: false,
      title: heHub.title,
      description: heHub.description,
      keywords: heHub.keywords,
      locale: "he_IL",
    },
  },
  {
    path: "/math/:slug",
    component: () => import("../components/TopicPage.vue"),
    name: "mathTopic",
    meta: { requiresAuth: false },
  },
  {
    path: "/he/math/:slug",
    component: () => import("../components/TopicPage.vue"),
    name: "mathTopicHe",
    meta: { requiresAuth: false },
  },
  ...gradeRoutes,
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach(async (to) => {
  const user = await restoreSessionFromCookie();
  if (user) {
    return;
  }

  if (!to.matched.some((record) => record.meta.requiresAuth)) {
    // auth not required
    return;
  }

  return {
    path: "/login",
    query: { from: to.path },
  };
});

/**
 * After each navigation, update SEO meta tags
 */
router.afterEach((to) => {
  const seo = useSeo();

  // Extract SEO metadata from route
  const title = (to.meta.title as string) || "MathBoard";
  const description =
    (to.meta.description as string) ||
    "Math Whiteboard is an interactive board for live classroom lessons and solo AI-tutor practice.";
  const keywords =
    (to.meta.keywords as string) || "mathematics, online learning";

  // Always the apex domain. A request host such as localhost or www must
  // not become the canonical URL Google stores.
  const currentUrl = `${ORIGIN}${to.path}`;
  const onTopic = isMathTopicPath(to.path);
  const topicHit = onTopic ? topicFromPath(to.path) : null;
  let locale = (to.meta.locale as string) || "en_US";
  let pageTitle = title;
  let pageDescription = description;
  let pageKeywords = keywords;

  if (onTopic && topicHit) {
    const copy = topicHit.lang === "he" ? topicHit.topic.he : topicHit.topic.en;
    pageTitle = copy.title;
    pageDescription = copy.description;
    pageKeywords = copy.keywords;
    locale = topicHit.lang === "he" ? "he_IL" : "en_US";
  } else if (onTopic) {
    pageTitle = "Page not found — Math Whiteboard";
    pageDescription = "That address is not a Math Whiteboard page.";
    pageKeywords = "math whiteboard";
  }

  // Set page meta tags. Robots stay untouched on ordinary routes so a
  // prerendered noindex (login, lessons) survives client navigation.
  // Topic routes pin robots, and leaving one restores index on public pages.
  seo.setPageMeta({
    title: pageTitle,
    description: pageDescription,
    keywords: pageKeywords,
    url: currentUrl,
    canonicalUrl: currentUrl,
    locale,
    ...(onTopic
      ? {
          robots:
            topicHit?.topic.published === true ? INDEX_ROBOTS : "noindex, follow",
        }
      : {}),
  });

  const practicePair =
    to.path === "/practice" || to.path === HEBREW_PRACTICE_PATH;
  const onHub = to.path === "/math" || to.path === "/he/math";
  const gradeHit = gradeHubFromPath(to.path);
  if (onTopic && topicHit) {
    const en = `${ORIGIN}${mathTopicPath(topicHit.topic.slug, "en")}`;
    const he = `${ORIGIN}${mathTopicPath(topicHit.topic.slug, "he")}`;
    seo.setHreflangAlternates([
      { hreflang: "en", href: en },
      { hreflang: "he", href: he },
      { hreflang: "x-default", href: en },
    ]);
    const crumbs = topicBreadcrumbs(topicHit.topic.slug, topicHit.lang);
    if (crumbs.length) seo.setBreadcrumbs(crumbs);
    topicRobotsPinned = true;
  } else if (onTopic) {
    seo.setHreflangAlternates([{ hreflang: "en", href: currentUrl }]);
    topicRobotsPinned = true;
  } else {
    if (topicRobotsPinned) {
      topicRobotsPinned = false;
      if (INDEXABLE_PATHS.has(to.path)) {
        seo.setPageMeta({ robots: INDEX_ROBOTS });
      }
    }
    const alternates = gradeHit
      ? gradeHreflang(gradeHit.grade)
      : onHub
        ? HUB_HREFLANG
        : practicePair
          ? PRACTICE_HREFLANG
          : [{ hreflang: "en", href: currentUrl }];
    seo.setHreflangAlternates(alternates);
    if (gradeHit) {
      seo.setPageMeta({ robots: INDEX_ROBOTS });
    }
  }

  // Handle breadcrumbs if defined in route meta. Topic routes set theirs above.
  const breadcrumbs = to.meta.breadcrumbs as
    | Array<{ name: string; url: string }>
    | undefined;
  if (!(onTopic && topicHit)) {
    if (breadcrumbs) {
      seo.setBreadcrumbs(breadcrumbs);
    } else {
      document
        .querySelectorAll('script[type="application/ld+json"][data-seo-managed]')
        .forEach((node) => node.remove());
    }
  }

  trackMetaPixelPageView(
    to.fullPath,
    (to.meta.title as string | undefined) || undefined,
  );
  trackPageView(to.path);
});

export default router;
